/**
 * Page /admin/invitations — Livraison 6 (Bloc C).
 *
 * Permet à un admin ou super_admin de gérer les invitations magic-link
 * envoyées aux pros (admin / manager / agent) pour rejoindre son tenant.
 *
 * Refonte R9 (mai 2026) :
 *   - Header avec LogoMark Mail accent tech
 *   - Card glass NewInvitationForm avec form 3 champs (email/rôle/message) + hint info
 *   - Card glass liste invitations avec table dense
 *   - Actions inline (Renvoyer / Révoquer / Réactiver) en boutons texte tech ou urgent
 *
 * Logique métier 100% préservée :
 *   - api.admin.invitations.list/create/resend/revoke
 *   - INVITABLE_ROLES (admin/manager/agent uniquement — administres = self-service)
 *   - 4 statuts : pending (warning), accepted (success), expired (neutral), revoked (urgent)
 *   - actionFeedback success/error
 *   - selectedHint changeant selon le rôle sélectionné
 *   - showForm toggle, reset email/message après création réussie
 *   - Pending → boutons Renvoyer + Révoquer
 *   - Expired/Revoked → bouton Renvoyer / Réactiver
 *   - Accepted → date acceptation avec icône
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import {
  Card, Button, Input, Field, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import {
  Mail, Send, RotateCcw, XCircle, Plus, Clock, CheckCircle2, AlertCircle,
  FileDown, Upload, Users,
} from 'lucide-react';
import type {
  AdminInvitation,
  CreateInvitationRequest,
  InvitationStatus,
  UserRole,
  UserService,
} from '@roronoa-collectivites/shared-types';

const INVITABLE_ROLES: { value: UserRole; label: string; hint: string }[] = [
  { value: 'admin',   label: 'Administrateur',  hint: 'Maire ou adjoint — accès complet au tenant' },
  { value: 'manager', label: 'Chef de service', hint: 'DST, directeur scolaire… — son équipe + son module' },
  { value: 'agent',   label: 'Agent',           hint: 'Opérationnel terrain — son planning, ses interventions' },
];

// Services internes — obligatoire pour un chef de service ou un agent.
// Valeurs alignées sur invitations.service.ts (IMPORT_SERVICES).
const SERVICES: { value: string; label: string }[] = [
  { value: 'service_technique', label: 'Service technique / voirie' },
  { value: 'scolaire_cantine',  label: 'Scolaire et cantine' },
  { value: 'petite_enfance',    label: 'Petite enfance (crèche, ALSH)' },
  { value: 'urbanisme',         label: 'Urbanisme' },
  { value: 'associations',      label: 'Vie associative' },
  { value: 'grc',               label: 'Relation citoyen / accueil mairie' },
  { value: 'rh',                label: 'Ressources humaines' },
];

const STATUS_LABEL: Record<InvitationStatus, { label: string; variant: 'warning' | 'success' | 'urgent' | 'neutral' }> = {
  pending:  { label: 'En attente', variant: 'warning' },
  accepted: { label: 'Acceptée',   variant: 'success' },
  expired:  { label: 'Expirée',    variant: 'neutral' },
  revoked:  { label: 'Révoquée',   variant: 'urgent' },
};

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Administrateur',
  manager: 'Manager',
  agent: 'Agent',
  administre: 'Administré',
};

const SELECT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function InvitationsPage() {
  const [items, setItems] = useState<AdminInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.invitations.list();
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error || 'Erreur' : 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onRevoke(id: string) {
    setActionFeedback(null);
    try {
      await api.admin.invitations.revoke(id);
      setActionFeedback({ kind: 'success', message: 'Invitation révoquée.' });
      await load();
    } catch (err) {
      setActionFeedback({
        kind: 'error',
        message: err instanceof ApiException ? err.payload.error || 'Erreur' : 'Erreur réseau',
      });
    }
  }

  async function onResend(id: string) {
    setActionFeedback(null);
    try {
      await api.admin.invitations.resend(id);
      setActionFeedback({ kind: 'success', message: 'Email renvoyé. Un nouveau lien a été généré.' });
      await load();
    } catch (err) {
      setActionFeedback({
        kind: 'error',
        message: err instanceof ApiException ? err.payload.error || 'Erreur' : 'Erreur réseau',
      });
    }
  }

  return (
    <div className="animate-fade-in">
      {/* En-tête de page */}
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <LogoMark icon={Mail} accent="tech" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">Administration</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Invitations
            </h1>
            <p className="mt-0.5 text-sm text-ink-muted">
              Inviter un maire, un adjoint, un chef de service ou un agent à rejoindre votre collectivité.
              Les administrés s'inscrivent eux-mêmes via le portail public.
            </p>
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Inviter quelqu'un</span>
        </Button>
      </header>

      {actionFeedback && (
        <Alert
          variant={actionFeedback.kind === 'success' ? 'success' : 'error'}
          icon={actionFeedback.kind === 'success' ? CheckCircle2 : AlertCircle}
          className="mb-4"
        >
          {actionFeedback.message}
        </Alert>
      )}

      {showForm && (
        <NewInvitationForm
          onSubmitted={async () => {
            setShowForm(false);
            setActionFeedback({ kind: 'success', message: 'Invitation envoyée.' });
            await load();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <ImportEquipeCard onImported={() => { void load(); }} />

      <Card variant="glass" padded>
        <SectionEyebrow as="h2" className="mb-3">Invitations envoyées</SectionEyebrow>
        {loading && (
          <div className="py-6 text-center">
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
              role="status"
              aria-label="Chargement"
            />
            <p className="mt-3 text-sm text-ink-muted">Chargement…</p>
          </div>
        )}
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <div className="py-6 text-center">
            <div className="flex justify-center mb-2">
              <LogoMark icon={Mail} accent="tech" size="md" />
            </div>
            <p className="text-sm text-ink-muted">
              Aucune invitation envoyée. Cliquez sur "Inviter quelqu'un" pour commencer.
            </p>
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 backdrop-blur-glass text-left">
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Email</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Rôle</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Statut</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Envoyée par</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Expire le</th>
                  <th className="px-3 py-2 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60">
                    <td className="px-3 py-3 font-medium text-ink">{inv.email}</td>
                    <td className="px-3 py-3 text-ink-muted">{ROLE_LABEL[inv.role] ?? inv.role}</td>
                    <td className="px-3 py-3">
                      <Badge variant={STATUS_LABEL[inv.status].variant} size="sm">
                        {STATUS_LABEL[inv.status].label}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs text-ink-muted">{inv.invitedByEmail ?? '—'}</td>
                    <td className="px-3 py-3 text-xs text-ink-muted">
                      {new Date(inv.expiresAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {inv.status === 'pending' && (
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => onResend(inv.id)}
                            className="inline-flex items-center gap-1 rounded-r-md px-1 text-xs text-tech-fg transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            title="Régénérer un nouveau lien et renvoyer par email"
                          >
                            <Send className="h-3.5 w-3.5" aria-hidden="true" /> Renvoyer
                          </button>
                          <button
                            type="button"
                            onClick={() => onRevoke(inv.id)}
                            className="inline-flex items-center gap-1 rounded-r-md px-1 text-xs text-urgent transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                          >
                            <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Révoquer
                          </button>
                        </div>
                      )}
                      {inv.status === 'expired' && (
                        <button
                          type="button"
                          onClick={() => onResend(inv.id)}
                          className="inline-flex items-center gap-1 rounded-r-md px-1 text-xs text-tech-fg transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Réactiver (nouveau lien)
                        </button>
                      )}
                      {inv.status === 'revoked' && (
                        <button
                          type="button"
                          onClick={() => onResend(inv.id)}
                          className="inline-flex items-center gap-1 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Renvoyer
                        </button>
                      )}
                      {inv.status === 'accepted' && (
                        <span className="inline-flex items-center gap-1 text-xs text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                          {inv.acceptedAt ? new Date(inv.acceptedAt).toLocaleDateString('fr-FR') : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Formulaire de nouvelle invitation
// ============================================================================

function NewInvitationForm({
  onSubmitted,
  onCancel,
}: {
  onSubmitted: () => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'agent'>('agent');
  const [service, setService] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsService = role === 'manager' || role === 'agent';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateInvitationRequest = {
        email: email.trim(),
        role,
        ...(needsService && service ? { service: service as UserService } : {}),
        ...(message.trim() && { message: message.trim() }),
      };
      await api.admin.invitations.create(body);
      await onSubmitted();
      setEmail('');
      setService('');
      setMessage('');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error || 'Erreur' : 'Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  }

  const selectedHint = INVITABLE_ROLES.find((r) => r.value === role)?.hint;

  return (
    <Card variant="glass" padded className="mb-4 border-tech/40">
      <SectionEyebrow as="h2" className="mb-3">Nouvelle invitation</SectionEyebrow>
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        <Field label="Email de l'invité" htmlFor="inv-email" required>
          <Input
            id="inv-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@ville.fr"
            required
          />
        </Field>
        <Field label="Rôle" htmlFor="inv-role" required hint={selectedHint}>
          <select
            id="inv-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'manager' | 'agent')}
            className={SELECT_CLS}
          >
            {INVITABLE_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
        {needsService && (
          <Field
            label="Service"
            htmlFor="inv-service"
            required
            hint="Le périmètre du chef de service ou de l'agent."
          >
            <select
              id="inv-service"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className={SELECT_CLS}
              required
            >
              <option value="">— Choisir un service —</option>
              {SERVICES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field
          label="Message personnalisé (optionnel)"
          htmlFor="inv-message"
          hint="Apparaît dans l'email d'invitation. 500 caractères max."
        >
          <textarea
            id="inv-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Ex: Bienvenue dans l'équipe ! Tu seras référent voirie cette année."
            className={TEXTAREA_CLS}
          />
        </Field>
        <div className="rounded-r-lg border border-tech/30 bg-tech/5 backdrop-blur-glass p-3 text-xs text-ink-muted">
          <Clock className="mr-1 inline h-3.5 w-3.5 text-tech-fg align-text-bottom" aria-hidden="true" />
          Lien valable <strong className="text-ink">7 jours</strong>. Un email avec lien magique sera envoyé à l'adresse indiquée.
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={submitting || !email.trim()}>
            <Send className="h-4 w-4" aria-hidden="true" />
            {submitting ? 'Envoi…' : 'Envoyer l\'invitation'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================================
// L13.29 — Import d'équipe via fichier Excel
// ============================================================================

function ImportEquipeCard({ onImported }: { onImported: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    total: number;
    created: { ligne: number; email: string }[];
    errors: { ligne: number; email: string; raison: string }[];
  } | null>(null);

  async function telechargerModele() {
    setError(null);
    try {
      const blob = await api.admin.invitations.importTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele-import-equipe.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Téléchargement du modèle impossible.');
    }
  }

  async function importer() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.admin.invitations.importEquipe(file);
      setResult(r);
      setFile(null);
      if (r.created.length > 0) onImported();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Import impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="glass" padded className="mb-4">
      <SectionEyebrow as="h2" className="mb-2">
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          Importer une équipe (Excel)
        </span>
      </SectionEyebrow>
      <p className="mb-3 text-sm text-ink-muted">
        Invitez toute une équipe d'un coup depuis un fichier Excel. Colonnes attendues :{' '}
        <span className="font-medium text-ink">email, prenom, nom, role, service</span>.
        Chaque ligne reçoit un mail d'invitation ; le service est obligatoire pour les
        managers et agents (laissé vide pour un admin).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => void telechargerModele()}>
          <FileDown className="h-4 w-4" aria-hidden="true" />
          Télécharger le modèle
        </Button>
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-r-lg border border-border bg-surface/60 px-3 text-xs text-ink-muted transition-all hover:border-border-strong hover:text-ink">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setResult(null);
              setError(null);
            }}
          />
          {file ? file.name : 'Choisir un fichier…'}
        </label>
        <Button
          variant="primary"
          size="sm"
          disabled={!file || busy}
          onClick={() => void importer()}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {busy ? 'Import en cours…' : 'Importer'}
        </Button>
      </div>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mt-3">
          {error}
        </Alert>
      )}

      {result && (
        <div className="mt-3 space-y-2">
          <Alert
            variant={result.errors.length === 0 ? 'success' : 'warning'}
            icon={result.errors.length === 0 ? CheckCircle2 : AlertCircle}
          >
            {result.created.length} invitation(s) créée(s) sur {result.total} ligne(s).
            {result.errors.length > 0 && ` ${result.errors.length} ligne(s) en erreur.`}
          </Alert>
          {result.errors.length > 0 && (
            <div className="rounded-r-lg border border-border bg-surface/40 p-3 text-xs">
              <p className="mb-1.5 font-semibold text-ink">Lignes à corriger :</p>
              <ul className="space-y-1 text-ink-muted">
                {result.errors.map((e) => (
                  <li key={`${e.ligne}-${e.email}`}>
                    Ligne {e.ligne}
                    {e.email ? ` (${e.email})` : ''} — {e.raison}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
